import type { FieldError } from './error';
import { type LineItemFieldKey, type LineItemFieldModel } from './line-item';
import {
  type QuantityBreakFieldKey,
  type QuantityBreakFieldModel,
} from './quantity-break';
import type { AutoConfiguratorState } from './state';
import type { StaticDataSource } from './static-data';
import type { Value, ValueId, ValueStringType } from './value';

export const enum FieldAdornmentOption {
  dollarSign = 'dollarSign',
  dimensionUnit = 'dimensionUnit',
}

// Field Definitions
export interface FieldDefinitionBase {
  type: ValueStringType;

  leftAdornment?: FieldAdornmentOption;
  rightAdornment?: FieldAdornmentOption;

  isReadonly: boolean;
  isRequired?: boolean;
  validators?: FieldValidator[];
}

export interface NumberValidationDefinition {
  min?: number;
  max?: number;
}

export interface DecimalValidationDefinition {
  min?: number;
  max?: number;
}

export interface TextValidationDefinition {
  minLength?: number;
  maxLength?: number;
}

export interface SelectValidationDefinition {
  allowedValues?: string[];
}

export interface FieldDefinitionDecimal extends FieldDefinitionBase {
  type: 'decimal';
  validation?: DecimalValidationDefinition;
}

export interface FieldDefinitionNumber extends FieldDefinitionBase {
  type: 'number';
  validation?: NumberValidationDefinition;
}

export interface FieldDefinitionSelect extends FieldDefinitionBase {
  type: 'select';
  staticDataSource: StaticDataSource;
  validation?: SelectValidationDefinition;
}

export interface FieldDefinitionText extends FieldDefinitionBase {
  type: 'text';
  validation?: TextValidationDefinition;
}

// Description has complex override/restore behavior that requires a one-off implementation
export interface FieldDefinitionDescription extends FieldDefinitionBase {
  type: 'description';
  validation?: TextValidationDefinition;
}

export interface FieldDefinitionBoolean extends FieldDefinitionBase {
  type: 'boolean';
  validation?: never;
}

export type FieldDefinition =
  | FieldDefinitionDecimal
  | FieldDefinitionNumber
  | FieldDefinitionSelect
  | FieldDefinitionText
  | FieldDefinitionDescription
  | FieldDefinitionBoolean;

// Field IDs
type FieldIdBuilder<Model, Key> = {
  model: Model;
  key: Key;
};

export type LineItemFieldId = FieldIdBuilder<
  LineItemFieldModel,
  LineItemFieldKey
>;

export type QuantityBreakFieldId = FieldIdBuilder<
  QuantityBreakFieldModel,
  QuantityBreakFieldKey
>;

export type FieldId = LineItemFieldId | QuantityBreakFieldId;

// Fields
type FieldBuilder<Model, Key> = {
  model: Model;
  key: Key;
  definition: FieldDefinition;
};

export type LineItemField = FieldBuilder<LineItemFieldModel, LineItemFieldKey>;

export type QuantityBreakField = FieldBuilder<
  QuantityBreakFieldModel,
  QuantityBreakFieldKey
>;

export type Field = LineItemField | QuantityBreakField;

export interface FieldValidatorContext {
  state: AutoConfiguratorState;
  fieldId: FieldId;
  valueId: ValueId;
  field: Field;
  value: Value;
  getField: (fieldId: FieldId) => Field;
  getValue: (valueId: ValueId) => Value;
}

export type FieldValidator = (context: FieldValidatorContext) => FieldError[];

// Field Map
export type FieldMap = {
  lineItem: Record<LineItemFieldKey, LineItemField>;
  quantityBreak: Record<QuantityBreakFieldKey, QuantityBreakField>;
};
